import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { useTranslator } from '@/hooks/useTranslator';
import { CryptoRecipientStatus, RecipientResponse } from '@/types/recipient';
import { getRecipientBankName, getRecipientCurrency } from '@/utility/recipientDisplayAdapter';

interface Props {
  readonly title: string;
  readonly recipients?: readonly RecipientResponse[];

  onClick?(): void;
}

const cryptoStatusLabels: Record<CryptoRecipientStatus, string> = {
  [CryptoRecipientStatus.PENDING_REVIEW]: 'CRYPTO_STATUS_PENDING_REVIEW',
  [CryptoRecipientStatus.APPROVED]: 'CRYPTO_STATUS_APPROVED',
  [CryptoRecipientStatus.REJECTED]: 'CRYPTO_STATUS_REJECTED',
};

const cryptoStatusColors: Record<
  CryptoRecipientStatus,
  { foreground: string; background: string }
> = {
  [CryptoRecipientStatus.PENDING_REVIEW]: {
    foreground: 'status.pending.foreground',
    background: 'status.pending.background',
  },
  [CryptoRecipientStatus.APPROVED]: {
    foreground: 'status.completed.foreground',
    background: 'status.completed.background',
  },
  [CryptoRecipientStatus.REJECTED]: {
    foreground: 'status.failed.foreground',
    background: 'status.failed.background',
  },
};

export const ContactItem: React.FC<Props> = (props: Props): React.ReactElement => {
  const { title, recipients = [], onClick } = props;

  const tr = useTranslator();
  const visibleRecipients = React.useMemo(
    (): readonly RecipientResponse[] => recipients.slice(0, VISIBLE_ACCOUNTS_COUNT),
    [recipients],
  );

  return (
    <Box sx={[styles.container, onClick ? styles.clickable : null]} onClick={onClick}>
      <Typography variant="body2" color="text.primary">
        {title}
      </Typography>
      {visibleRecipients.map(
        (recipient: RecipientResponse, index: number): React.ReactElement => (
          <Box key={recipient.id} display="flex" alignItems="center" gap={1}>
            <Typography variant="body1" color="custom.LightGray" component="span">
              {`${getRecipientCurrency(recipient)} | ${getRecipientBankName(recipient) ?? recipient.type}`}
              {index < visibleRecipients.length - 1 ? ',' : ''}
            </Typography>
            {recipient.cryptoStatus != null && (
              <Box
                display="inline-flex"
                alignItems="center"
                px={0.75}
                py={0.25}
                borderRadius={1}
                bgcolor={cryptoStatusColors[recipient.cryptoStatus].background}>
                <Typography
                  variant="caption"
                  color={cryptoStatusColors[recipient.cryptoStatus].foreground}>
                  {tr(cryptoStatusLabels[recipient.cryptoStatus])}
                </Typography>
              </Box>
            )}
          </Box>
        ),
      )}
      {recipients.length > VISIBLE_ACCOUNTS_COUNT && (
        <Typography variant="body1" color="custom.LightGray">
          <Link color="inherit" component="span">
            <Typography variant="inherit" color="primary" component="span">
              {tr('MORE', recipients.length - VISIBLE_ACCOUNTS_COUNT)}
            </Typography>
          </Link>
        </Typography>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    borderRadius: 2,
    py: 3,
    px: 4,
  },
  clickable: {
    cursor: 'pointer',
  },
};

const VISIBLE_ACCOUNTS_COUNT = 2;
